import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Scale, BookOpen, Sparkles, Coins, Brain, Infinity as InfinityIcon, UserRoundCog, Play, Pause, Volume2 } from "lucide-react";
import { KindText } from "@/components/KindK";

const NARRATION_SRC = `${process.env.PUBLIC_URL || ""}/mykind_narration.mp3`;

const PILLARS = [
  { key: "sovereignty", icon: Crown, title: "Sovereignty", line: "You own your data, identity and creations. Nothing is sold behind your back — ever." },
  { key: "equality", icon: Scale, title: "Equality", line: "Region-fair pricing means everyone pays what's fair for where they live. One world, one price of dignity." },
  { key: "knowledge", icon: BookOpen, title: "Knowledge", line: "Ask anything, learn anything — AI answers, courses and a living encyclopedia the community grows together." },
  { key: "fun", icon: Sparkles, title: "Fun", line: "Communities, travel, meals, events — live, share and play across borders as one family." },
  { key: "earn", icon: Coins, title: "Earn", line: "Turn your work, referrals, knowledge and kindness into credits and real earnings. Everyone benefits from the network." },
];

const INTELLIGENCE = [
  { key: "ai-native", icon: Brain, title: "AI-Native", line: "Intelligence runs through every corner — search, health, learning, money and creation — not bolted on, but built in." },
  { key: "autonomous", icon: InfinityIcon, title: "Autonomous & Self-Evolving", line: "6th-generation software that improves itself and adapts to the world, so the app keeps getting better on its own." },
  { key: "personalised", icon: UserRoundCog, title: "Truly Personalised", line: "It learns your context, language, region and goals — shaping every answer and price around you." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};

export default function HowItWorks() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
    else { a.pause(); setPlaying(false); }
  };

  useEffect(() => {
    const onPlayStory = () => {
      const sec = document.getElementById("how-it-works");
      if (sec) sec.scrollIntoView({ behavior: "smooth", block: "start" });
      const a = audioRef.current;
      if (a) { a.currentTime = 0; a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
    };
    window.addEventListener("mykind-play-story", onPlayStory);
    return () => window.removeEventListener("mykind-play-story", onPlayStory);
  }, []);

  return (
    <section id="how-it-works" className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 sm:pt-28 scroll-mt-24" data-testid="how-it-works-section">
      <audio
        ref={audioRef}
        src={NARRATION_SRC}
        preload="none"
        data-testid="narration-audio"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
        <motion.div custom={0} variants={fadeUp} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand">
          <Sparkles size={13} /> How My Kind works
        </motion.div>
        <motion.h2 custom={1} variants={fadeUp} className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 leading-[1.05] max-w-3xl">
          One app for everything — built on five promises.
        </motion.h2>
        <motion.p custom={2} variants={fadeUp} className="text-sec text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">
          <KindText text="My Kind" /> is AI-native, self-evolving software with a single purpose: give <b className="text-[var(--rud-text)]">every
          person the power to do everything</b> — while they own their data and earn from it. Here's the heart of it.
        </motion.p>
        <motion.button
          custom={3} variants={fadeUp}
          type="button"
          onClick={toggle}
          data-testid="play-story-btn"
          className="mt-5 inline-flex items-center gap-2 btn-primary rounded-full h-11 px-5 text-sm font-medium"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
          {playing ? "Pause the story" : "▶ Play the story"}
          <Volume2 size={15} className="opacity-70" />
        </motion.button>
      </motion.div>

      {/* Five pillars */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.key}
            data-testid={`hiw-pillar-${p.key}`}
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            custom={i} variants={fadeUp}
            className="group rud-card rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1 hover:border-brand/50 transition-all"
          >
            <div className="absolute -top-12 -right-6 w-40 h-40 brand-glow rounded-full pointer-events-none opacity-0 group-hover:opacity-40 transition-opacity" />
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand grid place-items-center">
                <p.icon size={22} strokeWidth={1.8} />
              </div>
              <h3 className="font-serif text-2xl tracking-tight mt-4 group-hover:text-brand transition-colors">{p.title}</h3>
              <p className="text-sec text-sm mt-2 leading-relaxed">{p.line}</p>
            </div>
          </motion.div>
        ))}

        {/* Closing tile */}
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          custom={PILLARS.length} variants={fadeUp}
          className="rounded-2xl p-6 border border-brand/30 bg-brand/[0.06] relative overflow-hidden flex flex-col justify-center"
          data-testid="hiw-pillar-closing"
        >
          <div className="absolute -bottom-10 -left-6 w-44 h-44 brand-glow rounded-full pointer-events-none opacity-50" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.2em] text-brand">The result</div>
            <p className="font-serif text-2xl leading-snug mt-2">
              Sovereignty, Equality, Knowledge, Fun & Earning — <span className="text-brand">for all</span>.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Intelligence row */}
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
        custom={0} variants={fadeUp}
        className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand"
      >
        <Brain size={13} /> Intelligent by design
      </motion.div>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {INTELLIGENCE.map((it, i) => (
          <motion.div
            key={it.key}
            data-testid={`hiw-intel-${it.key}`}
            initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            custom={i} variants={fadeUp}
            className="flex items-start gap-3 rounded-2xl border border-rud bg-white/[0.02] p-5 hover:border-brand/40 transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0">
              <it.icon size={19} strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[15px] font-semibold text-[var(--rud-text)]">{it.title}</div>
              <p className="text-sec text-[13px] mt-1 leading-relaxed">{it.line}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
